import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import WaveformV2 from '@/components/WaveformV2';
import VUMeter from '@/components/VUMeter';
import ProducerView from '@/pages/ProducerView';
import SettingsModal from '@/components/SettingsModal';
import MIDIDebugPanel from '@/components/MIDIDebugPanel';
import { MIDILearnProvider, useMIDILearn } from '@/components/MIDILearnManager';
import { MIDILearnButton, MIDILearnSlider } from '@/components/MIDILearnButton';
import BeatDetector from '@/utils/BeatDetector';
import { 
  Play, Pause, SkipBack, Volume2, Music, Disc3, Plus, X, 
  ChevronLeft, ChevronRight, Repeat, Settings, Sliders, Target, Radio, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Reverse Camelot to Musical Key mapping
const CAMELOT_TO_KEY = {
  "1A": "G#m", "1B": "B",
  "2A": "D#m", "2B": "F#",
  "3A": "A#m", "3B": "C#",
  "4A": "Fm", "4B": "G#",
  "5A": "Cm", "5B": "D#",
  "6A": "Gm", "6B": "A#",
  "7A": "Dm", "7B": "F",
  "8A": "Am", "8B": "C",
  "9A": "Em", "9B": "G",
  "10A": "Bm", "10B": "D",
  "11A": "F#m", "11B": "A",
  "12A": "C#m", "12B": "E"
};

function DJMixerProContent() {
  // MIDI Learn context
  const { isLearning, startLearning, stopLearning, waitingForControl, midiDevices, selectedDevice, registerCallback } = useMIDILearn();

  // Track library state
  const [tracks, setTracks] = useState([]);
  const [selectedLibraryTrack, setSelectedLibraryTrack] = useState(null);
  const [showBrowser, setShowBrowser] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [orientation, setOrientation] = useState('landscape');
  const [layoutMode, setLayoutMode] = useState('battle'); // 'battle' or 'producer'
  const [focusedDeck, setFocusedDeck] = useState('left'); // Track which deck has focus
  const [showSettings, setShowSettings] = useState(false);
  
  // Audio settings state
  const [audioSettings, setAudioSettings] = useState({
    latency: 10, // ms
    audioDriver: 'WebAudio', // 'WebAudio', 'ASIO', 'CoreAudio'
    sampleRate: 44100,
    bufferSize: 256,
    syncTolerance: 0.1, // seconds for sync detection
  });
  
  const mixerRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const leftBeatDetector = useRef(null);
  const rightBeatDetector = useRef(null);
  const audioContextRef = useRef(null);

  // Detect device type and orientation
  useEffect(() => {
    const detectDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1280 && isTouchDevice);
      setOrientation(height > width ? 'portrait' : 'landscape');
      
      // Hide browser on mobile by default
      if (width < 768) {
        setShowBrowser(false);
      }
    };

    detectDevice();
    window.addEventListener('resize', detectDevice);
    window.addEventListener('orientationchange', detectDevice);

    return () => {
      window.removeEventListener('resize', detectDevice);
      window.removeEventListener('orientationchange', detectDevice);
    };
  }, []);

  // Deck state
  const [leftDeck, setLeftDeck] = useState({
    track: null,
    playing: false,
    volume: 75,
    pitch: 0, // -50 to +50
    eqHigh: 50,
    eqMid: 50,
    eqLow: 50,
    loopStart: null,
    loopEnd: null,
    looping: false,
    cuePoints: [], // Array of {time, id}
  });

  const [rightDeck, setRightDeck] = useState({
    track: null,
    playing: false,
    volume: 75,
    pitch: 0,
    eqHigh: 50,
    eqMid: 50,
    eqLow: 50,
    loopStart: null,
    loopEnd: null,
    looping: false,
    cuePoints: [],
  });

  const [crossfader, setCrossfader] = useState(50);
  const [masterVolume, setMasterVolume] = useState(80);

  // Audio refs
  const leftAudioRef = useRef(null);
  const rightAudioRef = useRef(null);

  // Drag and drop state
  const [draggedTrack, setDraggedTrack] = useState(null);

  useEffect(() => {
    loadTracks();
  }, []);

  const loadTracks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tracks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTracks(response.data);
    } catch (error) {
      console.error('Error loading tracks:', error);
      toast.error('Failed to load tracks');
    }
  };

  const loadTrackToDeck = async (track, deck) => {
    try {
      console.log(`🎵 Loading track to ${deck} deck:`, track);
      const audioElement = deck === 'left' ? leftAudioRef.current : rightAudioRef.current;

      if (!audioElement) {
        throw new Error('Audio element not found');
      }

      audioElement.pause();
      audioElement.crossOrigin = 'anonymous';
      
      const audioUrl = `${API}/library/audio/${track.id}`;
      console.log('Audio URL:', audioUrl);
      audioElement.src = audioUrl;
      
      // CRITICAL: Preload for instant playback
      audioElement.preload = 'auto';
      audioElement.load();

      // Wait for audio to be ready for instant playback
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error('⏱️ Audio load timeout after 10s');
          reject(new Error('Load timeout - audio took too long to load'));
        }, 10000);
        
        audioElement.addEventListener('canplaythrough', () => {
          console.log('✓ Audio ready (canplaythrough)');
          clearTimeout(timeout);
          resolve();
        }, { once: true });
        
        audioElement.addEventListener('error', (e) => {
          console.error('❌ Audio load error:', e, audioElement.error);
          clearTimeout(timeout);
          const errorMsg = audioElement.error ? 
            `Error code ${audioElement.error.code}: ${audioElement.error.message}` : 
            'Unknown audio error';
          reject(new Error(errorMsg));
        }, { once: true });
      });

      const newDeckState = {
        track,
        playing: false,
        volume: 75,
        pitch: 0,
        eqHigh: 50,
        eqMid: 50,
        eqLow: 50,
        loopStart: null,
        loopEnd: null,
        looping: false,
        cuePoints: [],
      };

      if (deck === 'left') {
        setLeftDeck(newDeckState);
        console.log('✓ Left deck state updated');
      } else {
        setRightDeck(newDeckState);
        console.log('✓ Right deck state updated');
      }

      console.log('✅ Track loaded successfully!');
      toast.success(`Loaded "${track.title}" to ${deck.toUpperCase()} deck - READY! 🎵`);
      
      // BEAT DETECTION: Analyze track for kicks and snares (async, non-blocking)
      if (track.bpm && track.bpm > 0) {
        analyzeBeatGrid(audioElement, track, deck);
      }
      
    } catch (error) {
      console.error('❌ Error loading track:', error);
      toast.error(`Failed to load track: ${error.message}`);
    }
  };

  /**
   * Analyze audio track to detect beat grid
   */
  const analyzeBeatGrid = async (audioElement, track, deck) => {
    try {
      console.log(`🔍 [${deck.toUpperCase()}] Analyzing beat grid...`);
      
      // Initialize Audio Context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      
      // Fetch audio file and decode
      const response = await fetch(audioElement.src);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Initialize beat detector for this deck
      const beatDetector = new BeatDetector(audioContext);
      
      // Analyze beats
      const result = await beatDetector.analyzeTrack(audioBuffer, track.bpm);
      
      if (result.success) {
        // Store beat detector
        if (deck === 'left') {
          leftBeatDetector.current = beatDetector;
        } else {
          rightBeatDetector.current = beatDetector;
        }
        
        console.log(`✅ [${deck.toUpperCase()}] Beat grid ready: ${result.beatGrid.length} beats, ${result.kickTimes.length} kicks`);
        toast.success(`🎯 Beat grid analyzed for ${deck} deck!`, { duration: 2000 });
      }
      
    } catch (error) {
      console.warn(`⚠️ [${deck.toUpperCase()}] Beat detection failed:`, error.message);
      // Non-critical error, sync will still work without beat detection
    }
  };

  const togglePlay = (deck) => {
    const audioElement = deck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;

    if (deckState.playing) {
      audioElement?.pause();
      setDeckState({ ...deckState, playing: false });
    } else {
      // INSTANT PLAYBACK - no buffering
      if (audioElement) {
        audioElement.play().catch(err => {
          console.error('Playback error:', err);
          toast.error('Playback failed - ensure track is fully loaded');
        });
        setDeckState({ ...deckState, playing: true });
      }
    }
  };

  const updateVolume = (deck, value) => {
    const volume = value[0] / 100;
    const audioElement = deck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;

    if (audioElement) {
      const crossfadeEffect = deck === 'left' 
        ? (crossfader <= 50 ? 1 : (100 - crossfader) / 50)
        : (crossfader >= 50 ? 1 : crossfader / 50);
      
      audioElement.volume = volume * crossfadeEffect * (masterVolume / 100);
    }

    setDeckState({ ...deckState, volume: value[0] });
  };

  const updatePitch = (deck, value) => {
    const audioElement = deck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;

    if (audioElement) {
      // Pitch range: -50 to +50 => playbackRate: 0.5 to 1.5
      const playbackRate = 1 + (value[0] / 100);
      audioElement.playbackRate = playbackRate;
    }

    setDeckState({ ...deckState, pitch: value[0] });
  };

  // Calculate actual BPM based on pitch adjustment
  const getActualBPM = (originalBPM, pitchPercent) => {
    if (!originalBPM) return null;
    return Math.round(originalBPM * (1 + pitchPercent / 100) * 10) / 10;
  };

  const updateEQ = (deck, band, value) => {
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;

    setDeckState({ ...deckState, [`eq${band}`]: value[0] });
    
    // Note: Real EQ would require Web Audio API implementation
    toast.info(`${band} EQ: ${value[0]}%`);
  };

  const setCuePoint = (deck) => {
    const audioElement = deck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;

    if (!audioElement || !deckState.track) return;

    const currentTime = audioElement.currentTime;
    const newCuePoint = {
      id: `cue-${Date.now()}`,
      time: currentTime,
    };

    if (deckState.cuePoints.length < 4) {
      setDeckState({
        ...deckState,
        cuePoints: [...deckState.cuePoints, newCuePoint],
      });
      toast.success(`Cue point ${deckState.cuePoints.length + 1} set at ${currentTime.toFixed(2)}s`);
    } else {
      toast.warning('Maximum 4 cue points reached');
    }
  };

  const jumpToCue = (deck, cuePoint) => {
    const audioElement = deck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;
    
    if (audioElement) {
      // INSTANT CUE - no gap/lag
      audioElement.currentTime = cuePoint.time;
      
      // Auto-play from cue point (DJ behavior)
      if (!deckState.playing) {
        audioElement.play().catch(err => console.error('Cue play error:', err));
        setDeckState({ ...deckState, playing: true });
      }
    }
  };

  const clearCuePoints = (deck) => {
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;
    setDeckState({ ...deckState, cuePoints: [] });
    toast.info('Cue points cleared');
  };

  const setLoopPoint = (deck, pointType) => {
    const audioElement = deck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;

    if (!audioElement) return;

    const currentTime = audioElement.currentTime;

    if (pointType === 'start') {
      setDeckState({ ...deckState, loopStart: currentTime });
      toast.info(`Loop start: ${currentTime.toFixed(2)}s`);
    } else {
      setDeckState({ ...deckState, loopEnd: currentTime });
      toast.info(`Loop end: ${currentTime.toFixed(2)}s`);
    }
  };

  const toggleLoop = (deck) => {
    const audioElement = deck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const deckState = deck === 'left' ? leftDeck : rightDeck;
    const setDeckState = deck === 'left' ? setLeftDeck : setRightDeck;

    if (!audioElement || deckState.loopStart === null || deckState.loopEnd === null) {
      toast.warning('Set loop start and end points first');
      return;
    }

    const newLooping = !deckState.looping;
    setDeckState({ ...deckState, looping: newLooping });

    if (newLooping) {
      // Add time update listener for looping
      audioElement.addEventListener('timeupdate', () => {
        if (deckState.looping && audioElement.currentTime >= deckState.loopEnd) {
          audioElement.currentTime = deckState.loopStart;
        }
      });
      toast.success('Loop enabled');
    } else {
      toast.info('Loop disabled');
    }
  };

  const syncBPM = () => {
    if (!leftDeck.track || !rightDeck.track) {
      toast.warning('Load tracks on both decks first');
      return;
    }

    if (!leftDeck.track.bpm || !rightDeck.track.bpm) {
      toast.warning('BPM data not available for one or both tracks');
      return;
    }

    const bpmRatio = rightDeck.track.bpm / leftDeck.track.bpm;
    const pitchAdjust = (bpmRatio - 1) * 100;

    updatePitch('right', [pitchAdjust]);
    toast.success(`Synced BPMs! Right deck pitch: ${pitchAdjust.toFixed(1)}%`);
  };

  // Advanced sync functions
  const calculateBeatOffset = (sourceDeck, targetDeck) => {
    const sourceAudio = sourceDeck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const targetAudio = targetDeck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    
    if (!sourceAudio || !targetAudio) return null;
    
    const sourceDeckState = sourceDeck === 'left' ? leftDeck : rightDeck;
    const targetDeckState = targetDeck === 'left' ? leftDeck : rightDeck;
    
    if (!sourceDeckState.track?.bpm || !targetDeckState.track?.bpm) return null;
    
    // Get current playback times
    const sourceTime = sourceAudio.currentTime;
    const targetTime = targetAudio.currentTime;
    
    // Calculate beat length in seconds
    const sourceBeatLength = 60 / (sourceDeckState.track.bpm * (1 + sourceDeckState.pitch / 100));
    const targetBeatLength = 60 / (targetDeckState.track.bpm * (1 + targetDeckState.pitch / 100));
    
    // Find position within current beat
    const sourcePosition = (sourceTime % sourceBeatLength) / sourceBeatLength;
    const targetPosition = (targetTime % targetBeatLength) / targetBeatLength;
    
    // Calculate offset (how far behind/ahead target is from source)
    let offset = targetPosition - sourcePosition;
    
    // Normalize to [-0.5, 0.5] range (half beat window)
    if (offset > 0.5) offset -= 1;
    if (offset < -0.5) offset += 1;
    
    return {
      offsetBeats: offset,
      offsetSeconds: offset * targetBeatLength,
      sourceBeatLength,
      targetBeatLength
    };
  };

  const syncDeckTo = async (sourceDeck, targetDeck) => {
    console.log(`🎯 SYNC: ${targetDeck.toUpperCase()} → ${sourceDeck.toUpperCase()}`);
    
    const sourceAudio = sourceDeck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const targetAudio = targetDeck === 'left' ? leftAudioRef.current : rightAudioRef.current;
    const sourceDeckState = sourceDeck === 'left' ? leftDeck : rightDeck;
    const targetDeckState = targetDeck === 'left' ? leftDeck : rightDeck;
    const sourceBeatDetector = sourceDeck === 'left' ? leftBeatDetector.current : rightBeatDetector.current;
    const targetBeatDetector = targetDeck === 'left' ? leftBeatDetector.current : rightBeatDetector.current;
    
    if (!sourceAudio || !targetAudio) {
      toast.error('Audio elements not ready');
      return;
    }
    
    if (!sourceDeckState.track || !targetDeckState.track) {
      toast.warning('Load tracks on both decks first');
      return;
    }
    
    if (!sourceDeckState.playing) {
      toast.warning(`${sourceDeck.toUpperCase()} deck must be playing first`);
      return;
    }
    
    // === STEP 1: SYNC BPM ===
    if (sourceDeckState.track.bpm && targetDeckState.track.bpm) {
      const bpmRatio = sourceDeckState.track.bpm / targetDeckState.track.bpm;
      const pitchAdjust = (bpmRatio - 1) * 100;
      const totalPitch = pitchAdjust + sourceDeckState.pitch;
      
      updatePitch(targetDeck, [totalPitch]);
      console.log(`  ✓ BPM: ${targetDeck} pitch → ${totalPitch.toFixed(1)}%`);
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // === STEP 2: ADVANCED BEAT GRID SYNC (if available) ===
    const sourceTime = sourceAudio.currentTime;
    const targetTime = targetAudio.currentTime;
    
    if (sourceBeatDetector && targetBeatDetector) {
      console.log('  🎯 Using BEAT GRID analysis for precision sync');
      
      // Find nearest beat in source
      const sourceNearestBeat = sourceBeatDetector.findNearestBeat(sourceTime);
      const sourceBeatPhase = sourceBeatDetector.getBeatPhase(sourceTime);
      
      // Find which beat target should be on
      const targetCurrentBeat = Math.floor(targetTime / (60 / (targetDeckState.track.bpm * (1 + targetDeckState.pitch / 100))));
      
      // Calculate the target time using detected beat grid
      const targetBeatTimes = targetBeatDetector.beatGrid;
      if (targetBeatTimes && targetBeatTimes[targetCurrentBeat]) {
        const targetBeatStart = targetBeatTimes[targetCurrentBeat];
        const nextBeatStart = targetBeatTimes[targetCurrentBeat + 1] || (targetBeatStart + 0.469);
        const beatLength = nextBeatStart - targetBeatStart;
        
        // Apply source's phase to target's beat
        const newTargetTime = targetBeatStart + (sourceBeatPhase * beatLength);
        const timeDiff = newTargetTime - targetTime;
        const timeDiffMs = timeDiff * 1000;
        
        console.log(`  🎯 GRID SYNC: Source phase ${(sourceBeatPhase * 100).toFixed(0)}%`);
        console.log(`  📊 Offset: ${timeDiffMs.toFixed(1)}ms`);
        
        if (Math.abs(timeDiffMs) > 10) {
          targetAudio.currentTime = newTargetTime;
          console.log(`  ⚡ BEAT GRID CORRECTED: ${timeDiff > 0 ? '+' : ''}${timeDiffMs.toFixed(0)}ms`);
          
          toast.success(`🎯 BEAT GRID LOCKED! ${Math.abs(timeDiffMs).toFixed(0)}ms correction`, {
            duration: 2000,
            description: `Using analyzed kick/snare positions`
          });
        } else {
          toast.success(`✅ Already beat-locked!`, {
            duration: 1500,
            description: `Offset: ${Math.abs(timeDiffMs).toFixed(0)}ms`
          });
        }
      }
      
    } else {
      // === FALLBACK: STANDARD BPM-BASED SYNC ===
      console.log('  📊 Using BPM-based sync (beat grid not available)');
      
      const sourceBPM = sourceDeckState.track.bpm * (1 + sourceDeckState.pitch / 100);
      const targetBPM = targetDeckState.track.bpm * (1 + targetDeckState.pitch / 100);
      
      const sourceBeatLength = 60 / sourceBPM;
      const targetBeatLength = 60 / targetBPM;
      
      const sourceBeatPosition = sourceTime / sourceBeatLength;
      const sourceCurrentBeat = Math.floor(sourceBeatPosition);
      const sourcePhase = sourceBeatPosition - sourceCurrentBeat;
      
      const targetBeatPosition = targetTime / targetBeatLength;
      const targetCurrentBeat = Math.floor(targetBeatPosition);
      
      const newTargetTime = targetCurrentBeat * targetBeatLength + (sourcePhase * targetBeatLength);
      const timeDiff = newTargetTime - targetTime;
      const timeDiffMs = timeDiff * 1000;
      
      console.log(`  🎵 SOURCE: Beat ${sourceCurrentBeat} + ${(sourcePhase * 100).toFixed(0)}%`);
      console.log(`  🎵 TARGET: Beat ${targetCurrentBeat}`);
      console.log(`  📊 Offset: ${timeDiffMs.toFixed(1)}ms`);
      
      if (Math.abs(timeDiffMs) > 10) {
        targetAudio.currentTime = newTargetTime;
        console.log(`  ⚡ CORRECTED: ${timeDiff > 0 ? '+' : ''}${timeDiffMs.toFixed(0)}ms`);
        
        toast.success(`🎯 Beat-locked! ${Math.abs(timeDiffMs).toFixed(0)}ms correction`, {
          duration: 2000,
          description: `${targetDeck.toUpperCase()} synced to ${sourceDeck.toUpperCase()}`
        });
      } else {
        toast.success(`✅ Already beat-locked!`, {
          duration: 1500,
          description: `Offset: ${Math.abs(timeDiffMs).toFixed(0)}ms`
        });
      }
    }
    
    // === STEP 3: START PLAYBACK ===
    if (!targetDeckState.playing) {
      await new Promise(resolve => setTimeout(resolve, 50));
      togglePlay(targetDeck);
      console.log(`  ▶️ Started playback on ${targetDeck}`);
    }
  };

  // Continuous sync monitoring
  const enableContinuousSync = (sourceDeck, targetDeck) => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
    }
    
    console.log(`🔁 Continuous sync enabled: ${targetDeck} → ${sourceDeck}`);
    
    syncIntervalRef.current = setInterval(() => {
      const beatOffset = calculateBeatOffset(sourceDeck, targetDeck);
      
      if (beatOffset && Math.abs(beatOffset.offsetSeconds) > audioSettings.syncTolerance) {
        console.log(`⚠️ Drift detected: ${(beatOffset.offsetSeconds * 1000).toFixed(1)}ms - Auto-correcting...`);
        syncDeckTo(sourceDeck, targetDeck);
      }
    }, 2000); // Check every 2 seconds
  };

  const disableContinuousSync = () => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
      console.log('🔁 Continuous sync disabled');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, []);

  const updateCrossfader = (value) => {
    setCrossfader(value[0]);
    
    // Update both deck volumes
    updateVolume('left', [leftDeck.volume]);
    updateVolume('right', [rightDeck.volume]);
  };

  const updateMasterVolume = (value) => {
    setMasterVolume(value[0]);
    updateVolume('left', [leftDeck.volume]);
    updateVolume('right', [rightDeck.volume]);
  };

  // Drag and drop handlers
  const handleDragStart = (track) => {
    setDraggedTrack(track);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, deck) => {
    e.preventDefault();
    if (draggedTrack) {
      loadTrackToDeck(draggedTrack, deck);
      setDraggedTrack(null);
    }
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    const elem = mixerRef.current;
    
    if (!document.fullscreenElement) {
      elem.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Fullscreen error:', err);
        toast.error('Fullscreen not supported');
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Register MIDI callbacks on mount
  useEffect(() => {
    console.log('🎹 Registering MIDI callbacks...');
    
    // Volume controls
    registerCallback('volume', 'left', (normalizedValue) => {
      const value = Math.round(normalizedValue * 100);
      updateVolume('left', [value]);
    });
    registerCallback('volume', 'right', (normalizedValue) => {
      const value = Math.round(normalizedValue * 100);
      updateVolume('right', [value]);
    });
    
    // Pitch controls
    registerCallback('pitch', 'left', (normalizedValue) => {
      const value = Math.round((normalizedValue * 100) - 50); // -50 to +50
      updatePitch('left', [value]);
    });
    registerCallback('pitch', 'right', (normalizedValue) => {
      const value = Math.round((normalizedValue * 100) - 50);
      updatePitch('right', [value]);
    });
    
    // EQ controls
    ['low', 'mid', 'high'].forEach(band => {
      registerCallback(`eq-${band}`, 'left', (normalizedValue) => {
        const value = Math.round(normalizedValue * 100);
        updateEQ('left', band, [value]);
      });
      registerCallback(`eq-${band}`, 'right', (normalizedValue) => {
        const value = Math.round(normalizedValue * 100);
        updateEQ('right', band, [value]);
      });
    });
    
    // Crossfader
    registerCallback('crossfader', 'global', (normalizedValue) => {
      const value = Math.round(normalizedValue * 100);
      updateCrossfader([value]);
    });
    
    // Master volume
    registerCallback('master-volume', 'global', (normalizedValue) => {
      const value = Math.round(normalizedValue * 100);
      setMasterVolume(value);
    });
    
    // Play/Pause buttons
    registerCallback('play', 'left', () => togglePlay('left'));
    registerCallback('play', 'right', () => togglePlay('right'));
    
    // Cue buttons
    registerCallback('cue', 'left', () => setCuePoint('left'));
    registerCallback('cue', 'right', () => setCuePoint('right'));
    
    console.log('✓ MIDI callbacks registered');
  }, [registerCallback]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const currentDeck = focusedDeck === 'left' ? leftDeck : rightDeck;
      const audioElement = focusedDeck === 'left' ? leftAudioRef.current : rightAudioRef.current;

      switch(e.key.toLowerCase()) {
        case ' ': // Spacebar - Play/Pause
          e.preventDefault();
          togglePlay(focusedDeck);
          toast.info(`${focusedDeck.toUpperCase()} deck ${currentDeck.playing ? 'paused' : 'playing'}`);
          break;
        
        case '1': // Hot Cue 1
          if (e.shiftKey) {
            setCuePoint(focusedDeck);
          } else if (currentDeck.cuePoints[0]) {
            jumpToCue(focusedDeck, currentDeck.cuePoints[0]);
          }
          break;
        
        case '2': // Hot Cue 2
          if (e.shiftKey) {
            setCuePoint(focusedDeck);
          } else if (currentDeck.cuePoints[1]) {
            jumpToCue(focusedDeck, currentDeck.cuePoints[1]);
          }
          break;
        
        case '3': // Hot Cue 3
          if (e.shiftKey) {
            setCuePoint(focusedDeck);
          } else if (currentDeck.cuePoints[2]) {
            jumpToCue(focusedDeck, currentDeck.cuePoints[2]);
          }
          break;
        
        case '4': // Hot Cue 4
          if (e.shiftKey) {
            setCuePoint(focusedDeck);
          } else if (currentDeck.cuePoints[3]) {
            jumpToCue(focusedDeck, currentDeck.cuePoints[3]);
          }
          break;
        
        case 'q': // EQ High Kill
          e.preventDefault();
          updateEQ(focusedDeck, 'High', [currentDeck.eqHigh === 0 ? 50 : 0]);
          break;
        
        case 'w': // EQ Mid Kill
          e.preventDefault();
          updateEQ(focusedDeck, 'Mid', [currentDeck.eqMid === 0 ? 50 : 0]);
          break;
        
        case 'e': // EQ Low Kill
          e.preventDefault();
          updateEQ(focusedDeck, 'Low', [currentDeck.eqLow === 0 ? 50 : 0]);
          break;
        
        case 'arrowup': // Pitch up (temporary)
          e.preventDefault();
          if (audioElement) audioElement.playbackRate = 1.05;
          break;
        
        case 'arrowdown': // Pitch down (temporary)
          e.preventDefault();
          if (audioElement) audioElement.playbackRate = 0.95;
          break;
        
        case '[': // Loop Start
          setLoopPoint(focusedDeck, 'start');
          break;
        
        case ']': // Loop End
          setLoopPoint(focusedDeck, 'end');
          break;
        
        case 'l': // Toggle Loop
          toggleLoop(focusedDeck);
          break;
        
        case 'tab': // Switch focused deck
          e.preventDefault();
          setFocusedDeck(focusedDeck === 'left' ? 'right' : 'left');
          toast.info(`Focused: ${focusedDeck === 'left' ? 'RIGHT' : 'LEFT'} deck`);
          break;
        
        case 'escape': // Exit fullscreen
          if (isFullscreen) {
            toggleFullscreen();
          }
          break;
        
        case 's': // SYNC BPM
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            syncBPM();
          }
          break;
      }
    };

    const handleKeyUp = (e) => {
      const audioElement = focusedDeck === 'left' ? leftAudioRef.current : rightAudioRef.current;
      
      // Reset pitch bend
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const deckData = focusedDeck === 'left' ? leftDeck : rightDeck;
        if (audioElement) {
          audioElement.playbackRate = 1 + (deckData.pitch / 100);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [focusedDeck, leftDeck, rightDeck, isFullscreen]);

  const DeckComponent = ({ deck, deckData, audioRef }) => (
    <div className={isMobile || isTablet ? "w-full" : "flex-1"}>
      <Card 
        className="bg-gradient-to-br from-gray-900 to-gray-950 border-cyan-700/30 shadow-2xl touch-manipulation"
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, deck)}
      >
        <CardContent className={`${isFullscreen ? "p-3" : "p-3 md:p-4"}`}>
          {/* Track Display */}
          <div className={`bg-gray-800/50 rounded-lg ${isFullscreen ? 'p-2 mb-2 min-h-[70px]' : 'p-3 mb-3 min-h-[80px]'} border border-cyan-700/20`}>
            {deckData.track ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Disc3 className={`w-5 h-5 ${deckData.playing ? 'animate-spin text-cyan-400' : 'text-gray-400'}`} />
                  <h3 className="text-lg font-bold text-white truncate">{deckData.track.title}</h3>
                </div>
                <p className="text-sm text-gray-400">{deckData.track.artist}</p>
                <div className="flex gap-2 flex-wrap">
                  {deckData.track.bpm && (
                    <Badge className="bg-cyan-900/30 text-cyan-400 border-cyan-700/30">
                      {deckData.track.bpm} BPM
                    </Badge>
                  )}
                  {deckData.track.camelot_key && (
                    <>
                      <Badge className="bg-purple-900/30 text-purple-400 border-purple-700/30">
                        {deckData.track.camelot_key}
                      </Badge>
                      <Badge className="bg-pink-900/30 text-pink-400 border-pink-700/30">
                        {CAMELOT_TO_KEY[deckData.track.camelot_key] || deckData.track.key}
                      </Badge>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <Music className="w-12 h-12 mb-2" />
                <p className="text-sm">Drag & drop or select track</p>
              </div>
            )}
          </div>

          {/* Waveform */}
          <div className={`bg-black/50 rounded-lg p-2 ${isFullscreen ? 'mb-2' : 'mb-3'} border border-cyan-700/30`}>
            {deckData.track && audioRef.current ? (
              <WaveformV2
                audioElement={audioRef.current}
                color={deck === 'left' ? '#22d3ee' : '#f472b6'}
                progressColor={deck === 'left' ? '#06b6d4' : '#ec4899'}
                height={80}
              />
            ) : (
              <div className="flex items-center justify-center h-20 text-gray-600 text-xs bg-black/30 rounded">
                Waveform appears when track is loaded
              </div>
            )}
          </div>

          {/* Hot Cues */}
          <div className={isFullscreen ? "mb-2" : "mb-2"}>
            <div className={`flex items-center justify-between ${isFullscreen ? 'mb-2' : 'mb-1'}`}>
              <span className="text-xs text-gray-400 font-semibold">HOT CUES</span>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setCuePoint(deck)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-6">
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Button>
                <Button size="sm" onClick={() => clearCuePoints(deck)} className="bg-red-600 hover:bg-red-700 text-white text-xs h-6">
                  Clear
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1.5 md:gap-2">
              {[0, 1, 2, 3].map(i => (
                <Button
                  key={i}
                  onClick={() => deckData.cuePoints[i] && jumpToCue(deck, deckData.cuePoints[i])}
                  disabled={!deckData.cuePoints[i]}
                  className={`${deckData.cuePoints[i] ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-gray-700'} text-white text-xs touch-manipulation ${isTablet || isMobile ? 'h-10' : 'h-8'}`}
                >
                  {deckData.cuePoints[i] ? `CUE ${i + 1}` : `--- ${i + 1}`}
                </Button>
              ))}
            </div>
          </div>

          {/* Transport Controls */}
          <div className={`flex justify-center gap-2 md:gap-3 ${isFullscreen ? 'mb-2' : 'mb-2'}`}>
            <MIDILearnButton
              action="cue"
              deck={deck}
              label={`${deck.toUpperCase()} Deck - Cue/Restart`}
              onClick={() => { if (audioRef.current) audioRef.current.currentTime = 0; }}
              disabled={!deckData.track}
              className={`bg-gray-700 hover:bg-gray-600 text-white touch-manipulation ${isTablet || isMobile ? 'h-12 w-12' : ''}`}
            >
              <SkipBack className={isTablet || isMobile ? "w-5 h-5" : "w-4 h-4"} />
            </MIDILearnButton>
            <MIDILearnButton
              action="play"
              deck={deck}
              label={`${deck.toUpperCase()} Deck - Play/Pause`}
              onClick={() => togglePlay(deck)}
              disabled={!deckData.track}
              className={`${deckData.playing ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white touch-manipulation ${isTablet || isMobile ? 'w-24 h-12' : 'w-20'}`}
            >
              {deckData.playing ? <Pause className={isTablet || isMobile ? "w-6 h-6" : "w-5 h-5"} /> : <Play className={isTablet || isMobile ? "w-6 h-6" : "w-5 h-5"} />}
            </MIDILearnButton>
            <Button
              onClick={() => syncDeckTo(deck === 'left' ? 'right' : 'left', deck)}
              disabled={!deckData.track}
              className={`bg-orange-600 hover:bg-orange-700 text-white touch-manipulation ${isTablet || isMobile ? 'h-12 px-4' : 'px-3'}`}
              title={`Sync this deck to ${deck === 'left' ? 'RIGHT' : 'LEFT'} deck`}
            >
              <RefreshCw className={isTablet || isMobile ? "w-5 h-5" : "w-4 h-4"} />
            </Button>
          </div>

          {/* Loop Controls */}
          <div className={`${isFullscreen ? 'mb-2' : 'mb-2'} bg-gray-800/30 rounded-lg ${isFullscreen ? 'p-2' : 'p-2'}`}>
            <span className="text-xs text-gray-400 font-semibold mb-2 block">LOOP</span>
            <div className="flex gap-1.5 md:gap-2">
              <Button 
                size={isTablet || isMobile ? "default" : "sm"} 
                onClick={() => setLoopPoint(deck, 'start')} 
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs flex-1 touch-manipulation"
              >
                Start
              </Button>
              <Button 
                size={isTablet || isMobile ? "default" : "sm"} 
                onClick={() => setLoopPoint(deck, 'end')} 
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs flex-1 touch-manipulation"
              >
                End
              </Button>
              <Button 
                size={isTablet || isMobile ? "default" : "sm"} 
                onClick={() => toggleLoop(deck)} 
                className={`${deckData.looping ? 'bg-green-600' : 'bg-gray-600'} hover:bg-green-700 text-white text-xs flex-1 touch-manipulation`}
              >
                <Repeat className="w-3 h-3 mr-1" />
                {deckData.looping ? 'ON' : 'OFF'}
              </Button>
            </div>
          </div>

          {/* Pitch Control */}
          <div className={isFullscreen ? "mb-2" : "mb-2"}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400 font-semibold">PITCH / TEMPO</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-white">{deckData.pitch > 0 ? '+' : ''}{deckData.pitch}%</span>
                {deckData.track?.bpm && (
                  <Badge className="bg-green-900/30 text-green-400 border-green-700/30 text-xs">
                    ▶ {getActualBPM(deckData.track.bpm, deckData.pitch)} BPM
                  </Badge>
                )}
              </div>
            </div>
            <Slider
              value={[deckData.pitch]}
              onValueChange={(value) => updatePitch(deck, value)}
              min={-50}
              max={50}
              step={1}
              className="cursor-pointer"
            />
            {deckData.track?.bpm && (
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Original: {deckData.track.bpm} BPM</span>
                <span>Current: {getActualBPM(deckData.track.bpm, deckData.pitch)} BPM</span>
              </div>
            )}
          </div>

          {/* EQ Controls */}
          <div className={`${isFullscreen ? 'space-y-2' : 'space-y-2'} bg-gray-800/30 rounded-lg ${isFullscreen ? 'p-2' : 'p-2'}`}>
            <span className="text-xs text-gray-400 font-semibold block">EQ</span>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">HIGH</span>
                <span className="text-xs text-white">{deckData.eqHigh}%</span>
              </div>
              <Slider
                value={[deckData.eqHigh]}
                onValueChange={(value) => updateEQ(deck, 'High', value)}
                max={100}
                step={1}
                className="cursor-pointer"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">MID</span>
                <span className="text-xs text-white">{deckData.eqMid}%</span>
              </div>
              <Slider
                value={[deckData.eqMid]}
                onValueChange={(value) => updateEQ(deck, 'Mid', value)}
                max={100}
                step={1}
                className="cursor-pointer"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">LOW</span>
                <span className="text-xs text-white">{deckData.eqLow}%</span>
              </div>
              <Slider
                value={[deckData.eqLow]}
                onValueChange={(value) => updateEQ(deck, 'Low', value)}
                max={100}
                step={1}
                className="cursor-pointer"
              />
            </div>
          </div>

          {/* Volume Control */}
          <div className={isFullscreen ? "mt-4" : "mt-2"}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-semibold text-cyan-400">VOLUME</span>
              </div>
              <span className="text-sm font-bold text-white">{deckData.volume}%</span>
            </div>
            <MIDILearnSlider
              action="volume"
              deck={deck}
              label={`${deck.toUpperCase()} Deck - Volume`}
              onValueChange={(value) => updateVolume(deck, value)}
              max={100}
              step={1}
            >
              <Slider
                value={[deckData.volume]}
                onValueChange={(value) => updateVolume(deck, value)}
                max={100}
                step={1}
                className="cursor-pointer"
              />
            </MIDILearnSlider>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black flex flex-col touch-manipulation">
      {/* Header - Only show when NOT fullscreen */}
      {!isFullscreen && (
        <div className="border-b border-cyan-900/30 bg-black/60 backdrop-blur-xl shadow-xl shadow-cyan-500/10">
          <div className="container mx-auto px-3 md:px-6 py-2 md:py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-gradient-to-br from-cyan-500/30 to-purple-500/30 rounded-xl shadow-lg shadow-cyan-500/20">
                  <Disc3 className="w-5 h-5 md:w-6 md:h-6 text-cyan-300" />
                </div>
                <div>
                  <h1 className="text-base md:text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Harmonic Mixer
                  </h1>
                  <p className="text-xs text-gray-400 font-medium hidden sm:block">Professional DJ Software</p>
                </div>
              </div>
              <div className="flex gap-1.5 md:gap-2 items-center flex-wrap">
                {/* Back to Dashboard Button */}
                <Button
                  onClick={() => window.location.href = '/dashboard'}
                  className="bg-gray-700 hover:bg-gray-600 text-white font-bold touch-manipulation"
                  size="sm"
                >
                  <ChevronLeft className="w-3 h-3 md:w-4 md:h-4 md:mr-1" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Button>
                
                {/* MIDI Learn Button */}
                <Button
                  onClick={isLearning ? stopLearning : startLearning}
                  className={`${
                    isLearning 
                      ? 'bg-yellow-600 hover:bg-yellow-700 animate-pulse' 
                      : 'bg-pink-600 hover:bg-pink-700'
                  } text-white font-bold touch-manipulation`}
                  size="sm"
                >
                  <Radio className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                  <span className="hidden sm:inline">{isLearning ? 'STOP LEARN' : 'MIDI LEARN'}</span>
                </Button>

                {/* Settings Button */}
                <Button
                  onClick={() => setShowSettings(true)}
                  className="bg-gray-700 hover:bg-gray-600 text-white font-bold touch-manipulation"
                  size="sm"
                >
                  <Settings className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                  <span className="hidden sm:inline">SETTINGS</span>
                </Button>

                {/* Layout Switcher */}
                <select
                  value={layoutMode}
                  onChange={(e) => setLayoutMode(e.target.value)}
                  className="bg-gray-800 border border-cyan-500/30 text-white rounded px-2 md:px-3 py-1 text-xs md:text-sm font-semibold focus:outline-none focus:border-cyan-500 touch-manipulation"
                >
                  <option value="battle">⚔️ Battle DJ</option>
                  <option value="producer">🎛️ Producer</option>
                </select>

                {!isMobile && (
                  <Button
                    onClick={toggleFullscreen}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold touch-manipulation"
                    size="sm"
                  >
                    <span className="hidden md:inline">FULLSCREEN</span>
                  </Button>
                )}
                {!isMobile && (
                  <Button
                    onClick={() => setShowBrowser(!showBrowser)}
                    className="bg-gray-700 hover:bg-gray-600 text-white touch-manipulation"
                    size="sm"
                  >
                    {showBrowser ? <ChevronLeft className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </Button>
                )}
                <Button
                  onClick={() => window.location.href = '/'}
                  className="bg-gray-700 hover:bg-gray-600 text-white touch-manipulation"
                  size="sm"
                >
                  ←
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div 
        ref={mixerRef}
        className={`flex-1 flex overflow-hidden ${isMobile ? 'flex-col' : ''} ${
          isFullscreen 
            ? 'bg-gradient-to-br from-black via-gray-950 to-gray-900' 
            : ''
        }`}
        style={isFullscreen ? { height: '100vh', width: '100vw' } : {}}
      >
        {/* Fullscreen Inner Bevel Border - Inside edges */}
        {isFullscreen && (
          <div className="absolute inset-4 rounded-2xl border-2 border-cyan-500/30 shadow-[inset_0_0_60px_rgba(6,182,212,0.15)] pointer-events-none z-10"></div>
        )}

        {/* Fullscreen Exit Button */}
        {isFullscreen && (
          <div className="absolute top-6 right-6 z-50">
            <Button
              onClick={toggleFullscreen}
              className="bg-red-600 hover:bg-red-700 text-white font-bold touch-manipulation shadow-lg"
              size="sm"
            >
              <X className="w-4 h-4 mr-2" />
              Exit Fullscreen
            </Button>
          </div>
        )}

        {/* Track Browser Panel - Hidden on mobile/tablet */}
        {showBrowser && !isFullscreen && !isMobile && !isTablet && (
          <div className="w-64 lg:w-80 border-r border-gray-800 bg-gray-950/50 backdrop-blur-xl">
            <div className="p-3 border-b border-gray-800">
              <h2 className="text-sm lg:text-base font-bold text-white mb-1">Track Browser</h2>
              <p className="text-xs text-gray-400">Drag or double-click</p>
            </div>
            <ScrollArea className="h-[calc(100vh-180px)]">
              <div className="p-4 space-y-2">
                {tracks.length === 0 ? (
                  <div className="text-center py-12">
                    <Music className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No tracks in library</p>
                    <p className="text-xs text-gray-500 mt-1">Upload tracks from the main dashboard</p>
                  </div>
                ) : (
                  tracks.map((track) => (
                    <div
                      key={track.id}
                      draggable
                      onDragStart={() => handleDragStart(track)}
                      onDoubleClick={() => loadTrackToDeck(track, selectedLibraryTrack?.id === track.id ? 'right' : 'left')}
                      onClick={() => setSelectedLibraryTrack(track)}
                      className={`p-3 rounded-lg cursor-move border transition-all ${
                        selectedLibraryTrack?.id === track.id
                          ? 'bg-cyan-600/20 border-cyan-500'
                          : 'bg-gray-800/50 border-gray-700 hover:border-cyan-600/50'
                      }`}
                    >
                      <h4 className="font-semibold text-white text-sm truncate">{track.title}</h4>
                      <p className="text-xs text-gray-400 truncate">{track.artist}</p>
                      <div className="flex gap-2 mt-2">
                        {track.bpm && (
                          <Badge className="bg-cyan-900/30 text-cyan-400 text-xs">
                            {track.bpm} BPM
                          </Badge>
                        )}
                        {track.camelot_key && (
                          <Badge className="bg-purple-900/30 text-purple-400 text-xs">
                            {track.camelot_key}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
            
            {/* Quick Load Buttons */}
            {selectedLibraryTrack && (
              <div className="p-4 border-t border-gray-800 bg-gray-900/50">
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => loadTrackToDeck(selectedLibraryTrack, 'left')}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs"
                  >
                    Load to Left
                  </Button>
                  <Button
                    onClick={() => loadTrackToDeck(selectedLibraryTrack, 'right')}
                    className="bg-pink-600 hover:bg-pink-700 text-white text-xs"
                  >
                    Load to Right
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mixer Section */}
        <div className={`flex-1 ${isFullscreen ? 'overflow-hidden flex items-center justify-center p-6' : 'overflow-auto'}`}>
          {layoutMode === 'producer' ? (
            /* Producer Mode */
            <ProducerView
              leftDeck={leftDeck}
              rightDeck={rightDeck}
              leftAudioRef={leftAudioRef}
              rightAudioRef={rightAudioRef}
              tracks={tracks}
              selectedLibraryTrack={selectedLibraryTrack}
              setSelectedLibraryTrack={setSelectedLibraryTrack}
              loadTrackToDeck={loadTrackToDeck}
              togglePlay={togglePlay}
              updateVolume={updateVolume}
              updatePitch={updatePitch}
              updateEQ={updateEQ}
              setCuePoint={setCuePoint}
              jumpToCue={jumpToCue}
              clearCuePoints={clearCuePoints}
              setLoopPoint={setLoopPoint}
              toggleLoop={toggleLoop}
              crossfader={crossfader}
              updateCrossfader={updateCrossfader}
              masterVolume={masterVolume}
              updateMasterVolume={updateMasterVolume}
              syncBPM={syncBPM}
              focusedDeck={focusedDeck}
              setFocusedDeck={setFocusedDeck}
              isFullscreen={isFullscreen}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          ) : (
            /* Battle DJ Mode (Original) */
            <div className={`${
              isFullscreen 
                ? 'w-full h-full max-w-[1920px] flex items-center' 
                : 'container mx-auto px-2 sm:px-4 py-2 sm:py-4'
            }`}>
              {/* Responsive Grid: 1 column on mobile, 2 on tablet portrait, 3 on tablet landscape/desktop */}
              <div className={`w-full grid ${
                isMobile || (isTablet && orientation === 'portrait') 
                  ? 'grid-cols-1 gap-3' 
                  : isTablet && orientation === 'landscape'
                  ? 'grid-cols-2 gap-3'
                  : 'lg:grid-cols-3'
              } ${isFullscreen ? 'gap-4 h-full' : 'gap-3 md:gap-4'}`}>
              {/* Left Deck */}
              <DeckComponent 
                deck="left" 
                deckData={leftDeck} 
                audioRef={leftAudioRef}
              />

              {/* Center Controls */}
              <div className={`${isMobile || (isTablet && orientation === 'portrait') ? 'w-full' : 'flex-1'} flex flex-col justify-center ${isFullscreen ? 'gap-2' : 'gap-2'}`}>
                {/* VU Meters - Hide on mobile and tablet portrait */}
                {!isMobile && !(isTablet && orientation === 'portrait') && (
                  <div className={`flex justify-center ${isFullscreen ? 'gap-3 mb-2' : 'gap-3 mb-2'}`}>
                    <div className="flex flex-col items-center">
                      <VUMeter 
                        audioElement={leftAudioRef.current} 
                        label="LEFT"
                        color="#22d3ee"
                        height={isFullscreen ? 140 : isTablet ? 120 : 140}
                      />
                    </div>
                    <div className="flex flex-col items-center">
                      <VUMeter 
                        audioElement={rightAudioRef.current} 
                        label="RIGHT"
                        color="#f472b6"
                        height={isFullscreen ? 140 : isTablet ? 120 : 140}
                      />
                    </div>
                  </div>
                )}

                <Card className="bg-gradient-to-br from-gray-900 to-gray-950 border-purple-700/30 shadow-2xl touch-manipulation">
                  <CardContent className={isFullscreen ? "p-3" : "p-3 md:p-4"}>
                    {/* Master Volume */}
                    <div className={isFullscreen ? "mb-3" : "mb-3 md:mb-4"}>
                      <div className={`flex items-center justify-between ${isFullscreen ? 'mb-2 md:mb-3' : 'mb-2'}`}>
                        <div className="flex items-center gap-2">
                          <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-purple-400" />
                          <span className="text-xs md:text-sm font-bold text-purple-400">MASTER</span>
                        </div>
                        <span className="text-lg md:text-xl font-bold text-white">{masterVolume}%</span>
                      </div>
                      <Slider
                        value={[masterVolume]}
                        onValueChange={updateMasterVolume}
                        max={100}
                        step={1}
                        className="cursor-pointer touch-manipulation"
                      />
                    </div>

                    {/* Crossfader */}
                    <div className={`bg-gray-800/50 rounded-lg ${isFullscreen ? 'p-3' : 'p-3 md:p-4'} border border-purple-700/30 ${isFullscreen ? 'mb-3' : 'mb-3 md:mb-4'}`}>
                      <div className="text-center mb-3 md:mb-4">
                        <h3 className="text-base md:text-lg font-bold text-white mb-2">CROSSFADER</h3>
                        <div className="flex justify-between text-xs md:text-sm font-semibold">
                          <span className={`${crossfader < 50 ? 'text-cyan-400' : 'text-gray-500'}`}>LEFT</span>
                          <span className="text-purple-400">{crossfader}%</span>
                          <span className={`${crossfader > 50 ? 'text-pink-400' : 'text-gray-500'}`}>RIGHT</span>
                        </div>
                      </div>
                      <MIDILearnSlider
                        action="crossfader"
                        deck="master"
                        label="Crossfader"
                        onValueChange={updateCrossfader}
                        max={100}
                        step={1}
                      >
                        <Slider
                          value={[crossfader]}
                          onValueChange={updateCrossfader}
                          max={100}
                          step={1}
                          className="cursor-pointer touch-manipulation"
                        />
                      </MIDILearnSlider>
                    </div>

                    {/* Sync Button */}
                    <MIDILearnButton
                      action="sync"
                      deck="master"
                      label="Sync BPM"
                      onClick={syncBPM}
                      className={`w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white font-bold touch-manipulation ${isTablet || isMobile ? 'h-14' : ''}`}
                      size="lg"
                    >
                      <Target className={`${isTablet || isMobile ? 'w-6 h-6' : 'w-5 h-5'} mr-2`} />
                      SYNC BPM
                    </MIDILearnButton>
                  </CardContent>
                </Card>

                {/* Mini Track Library */}
                <Card className="bg-gradient-to-br from-gray-900 to-gray-950 border-purple-700/30 shadow-2xl touch-manipulation">
                  <CardContent className={isFullscreen ? "p-2" : "p-2 md:p-3"}>
                    <div className="flex items-center justify-between mb-2 md:mb-3">
                      <div className="flex items-center gap-2">
                        <Music className="w-3 h-3 md:w-4 md:h-4 text-purple-400" />
                        <h3 className="text-xs md:text-sm font-bold text-purple-400">TRACK LIBRARY</h3>
                      </div>
                      <Badge className="bg-purple-900/30 text-purple-400 border-purple-700/30 text-xs">
                        {tracks.length}
                      </Badge>
                    </div>
                    
                    <ScrollArea className={
                      isFullscreen ? "h-[200px]" : 
                      isTablet ? "h-[180px]" :
                      isMobile ? "h-[200px]" : "h-[240px]"
                    }>
                      <div className="space-y-1">
                        {tracks.length === 0 ? (
                          <div className="text-center py-8 text-gray-500 text-xs">
                            <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No tracks in library</p>
                            <p className="text-xs mt-1">Upload from main dashboard</p>
                          </div>
                        ) : (
                          tracks.map((track) => (
                            <div
                              key={track.id}
                              draggable
                              onDragStart={() => handleDragStart(track)}
                              onClick={() => setSelectedLibraryTrack(track)}
                              onDoubleClick={() => loadTrackToDeck(track, selectedLibraryTrack?.id === track.id ? 'right' : 'left')}
                              className={`p-2 rounded-md cursor-pointer border transition-all ${
                                selectedLibraryTrack?.id === track.id
                                  ? 'bg-purple-600/20 border-purple-500'
                                  : 'bg-gray-800/30 border-gray-700/30 hover:border-purple-600/50 hover:bg-gray-800/50'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-white text-xs truncate leading-tight">
                                    {track.title}
                                  </h4>
                                  <p className="text-xs text-gray-400 truncate mt-0.5">
                                    {track.artist}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  {track.bpm && (
                                    <Badge className="bg-cyan-900/30 text-cyan-400 border-cyan-700/30 text-xs px-1.5 py-0">
                                      {track.bpm}
                                    </Badge>
                                  )}
                                  {track.camelot_key && (
                                    <Badge className="bg-pink-900/30 text-pink-400 border-pink-700/30 text-xs px-1.5 py-0">
                                      {CAMELOT_TO_KEY[track.camelot_key] || track.key}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>

                    {selectedLibraryTrack && (
                      <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-gray-700/30">
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            onClick={() => loadTrackToDeck(selectedLibraryTrack, 'left')}
                            className={`bg-cyan-600 hover:bg-cyan-700 text-white text-xs touch-manipulation ${isTablet || isMobile ? 'h-11' : 'h-8'}`}
                            size="sm"
                          >
                            Load Left
                          </Button>
                          <Button
                            onClick={() => loadTrackToDeck(selectedLibraryTrack, 'right')}
                            className={`bg-pink-600 hover:bg-pink-700 text-white text-xs touch-manipulation ${isTablet || isMobile ? 'h-11' : 'h-8'}`}
                            size="sm"
                          >
                            Load Right
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Deck */}
              <DeckComponent 
                deck="right" 
                deckData={rightDeck} 
                audioRef={rightAudioRef}
              />
            </div>
          </div>
          )}
          {/* End Battle DJ / Producer conditional */}
        </div>
      </div>

      {/* Audio Elements */}
      <audio ref={leftAudioRef} />
      <audio ref={rightAudioRef} />

      {/* Settings Modal */}
      <SettingsModal open={showSettings} onOpenChange={setShowSettings} />

      {/* MIDI Learn Mode Indicator */}
      {isLearning && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-yellow-600 text-black px-6 py-3 rounded-lg shadow-2xl animate-pulse">
          <div className="flex items-center gap-2">
            <Radio className="w-5 h-5 animate-spin" />
            <span className="font-bold">
              {waitingForControl 
                ? `Touch controller for: ${waitingForControl.label}` 
                : 'MIDI LEARN MODE - Click any control to map'}
            </span>
          </div>
        </div>
      )}

      {/* MIDI Debug Panel */}
      <MIDIDebugPanel midiDevices={midiDevices} selectedDevice={selectedDevice} />
    </div>
  );
}

// Wrap with MIDI Learn Provider
export default function DJMixerPro() {
  return (
    <MIDILearnProvider>
      <DJMixerProContent />
    </MIDILearnProvider>
  );
}
